import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Star } from 'lucide-react';
import HeroPreview from './HeroPreview';

interface HeroProps {
  /** Where the primary call to action should send the visitor. */
  ctaHref: string;
}

const avatars = [
  { initials: 'JR', from: 'from-brand-500', to: 'to-brand-700' },
  { initials: 'SK', from: 'from-emerald-500', to: 'to-emerald-700' },
  { initials: 'TA', from: 'from-amber-400', to: 'to-amber-500' },
  { initials: 'LP', from: 'from-sky-500', to: 'to-sky-700' },
];

const Hero: React.FC<HeroProps> = ({ ctaHref }) => {
  const reduceMotion = useReducedMotion();
  const from = reduceMotion ? false : { opacity: 0, y: 18 };
  const to = { opacity: 1, y: 0 };
  const ease = [0.22, 1, 0.36, 1] as const;

  return (
    <section className="relative isolate overflow-hidden">
      {/* Backdrop: grid + two soft brand glows */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-grid-light bg-grid mask-fade-bottom dark:bg-grid-dark" />
        <div className="absolute -left-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-brand-500/15 blur-[110px] dark:bg-brand-600/25" />
        <div className="absolute -right-32 top-24 h-[30rem] w-[30rem] rounded-full bg-amber-400/15 blur-[110px] dark:bg-brand-400/15" />
      </div>

      <div className="section-shell grid items-center gap-14 py-16 sm:py-24 lg:grid-cols-12 lg:gap-10 lg:py-28">
        <div className="lg:col-span-6">
          <motion.div
            initial={from}
            animate={to}
            transition={{ duration: 0.7, delay: 0, ease }}
          >
            <Link
              to="/content"
              className="group inline-flex items-center gap-2 rounded-full border border-ink-900/[0.08] bg-base-100/80 py-1 pl-1 pr-3.5 text-sm shadow-soft backdrop-blur transition hover:border-brand-500/40 dark:border-white/10"
            >
              <span className="rounded-full bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white">
                New
              </span>
              <span className="text-base-content/70">Full GCSE &amp; A Level syllabus tracking</span>
              <ArrowRight className="h-3.5 w-3.5 text-base-content/40 transition group-hover:translate-x-0.5" />
            </Link>
          </motion.div>

          <motion.h1
            initial={from}
            animate={to}
            transition={{ duration: 0.7, delay: 0.08, ease }}
            className="display mt-7 text-balance text-5xl sm:text-6xl lg:text-[4.25rem]"
          >
            Tutoring that actually
            {' '}
            <span className="relative whitespace-nowrap">
              <span className="italic text-brand-600 dark:text-brand-400">moves the grade</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 300 12"
                className="absolute -bottom-1 left-0 h-2.5 w-full text-brand-500/40"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 8c60-5 120-6 180-4s90 4 116 2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </motion.h1>

          <motion.p
            initial={from}
            animate={to}
            transition={{ duration: 0.7, delay: 0.16, ease }}
            className="mt-6 max-w-xl text-lg text-base-content/65 sm:text-xl"
          >
            Hand-picked UK university tutors, a shared whiteboard built for real teaching,
            and progress you can see week by week. Matched to your child in under 24 hours.
          </motion.p>

          <motion.div
            initial={from}
            animate={to}
            transition={{ duration: 0.7, delay: 0.24, ease }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <Link
              to={ctaHref}
              className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 text-[15px] font-semibold text-white shadow-glow transition hover:bg-brand-700 active:scale-[0.98]"
            >
              Find your tutor
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to={ctaHref}
              className="inline-flex h-12 items-center justify-center rounded-xl border border-ink-900/[0.12] bg-base-100 px-6 text-[15px] font-semibold transition hover:border-ink-900/25 hover:bg-base-200 active:scale-[0.98] dark:border-white/15 dark:hover:border-white/30"
            >
              Become a Tütor
            </Link>
          </motion.div>

          <motion.div
            initial={from}
            animate={to}
            transition={{ duration: 0.7, delay: 0.32, ease }}
            className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-4"
          >
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2.5">
                {avatars.map((a) => (
                  <span
                    key={a.initials}
                    className={`grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br ring-2 ring-base-100 ${a.from} ${a.to} text-[11px] font-bold text-white`}
                  >
                    {a.initials}
                  </span>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-0.5 text-amber-500">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" />
                  ))}
                  <span className="ml-1.5 text-xs font-semibold text-base-content/80">4.9/5</span>
                </div>
                <p className="text-xs text-base-content/55">from 200+ families &amp; tutors</p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs font-medium text-base-content/60">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              Every tutor DBS checked &amp; interviewed
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-6">
          <HeroPreview />
        </div>
      </div>
    </section>
  );
};

export default Hero;
